import LazySection from "@/components/ui/LazySection";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/ui/PageHero";
import { Helmet } from "react-helmet-async";
import { Phone, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATIC_ASSETS, cdnImg } from "@/lib/imageUtils";
import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";
import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import HeroButtons from "@/components/ui/HeroButtons";
import { Link } from "@/lib/router-compat";
import LandingPageTemplate from "@/components/landing/LandingPageTemplate";
import type { LandingPageContent } from "@/components/admin/LandingPageEditor";
import type { HeroButton } from "@/hooks/useHeroContent";
import rvAppliancesAsset from "@/assets/parts-external/rv_appliances.webp.asset.json";
import rvFurnitureAsset from "@/assets/parts-external/rv_furniture.webp.asset.json";
import rvKitchensBathsAsset from "@/assets/parts-external/rv_kitchens_rv_bathrooms.webp.asset.json";
import rvLivingAsset from "@/assets/parts-external/rv_living.webp.asset.json";
import rvPartsImgAsset from "@/assets/parts-external/rv_parts.webp.asset.json";
import rvElectricalAsset from "@/assets/parts-external/rv_electrical.webp.asset.json";
import rvTowingAsset from "@/assets/parts-external/rv_towing.webp.asset.json";
import rvAccessoriesAsset from "@/assets/parts-external/rv_accessories.webp.asset.json";
import brandBlueoxAsset from "@/assets/parts-external/brand-blueox.png.asset.json";
import brandDicorAsset from "@/assets/parts-external/brand-dicor.png.asset.json";
import brandLippertAsset from "@/assets/parts-external/brand-lippert.png.asset.json";
import brandThetfordAsset from "@/assets/parts-external/brand-thetford.png.asset.json";
import funInSunAsset from "@/assets/parts-external/fun-in-sun.jpg.asset.json";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

const categories = [
  {
    title: "RV Appliances",
    image: rvAppliancesAsset.url,
    items: ["RV Washer & Dryer", "RV Refrigerators", "RV Microwave & Cooktops", "RV Dishwashers", "RV Air Conditioners", "RV Water Heaters"],
  },
  {
    title: "RV Furniture",
    image: rvFurnitureAsset.url,
    items: ["RV Mattresses & Bedding", "RV Folding Tables", "RV Chairs", "RV Trifold Sofas", "RV Recliners", "RV Theater Seating"],
  },
  {
    title: "RV Kitchens & Bathrooms",
    image: rvKitchensBathsAsset.url,
    items: ["RV Faucets", "RV Toilets", "RV Showers", "RV Plumbing", "RV Cooking Utensils", "RV Dishes & Cups"],
  },
  {
    title: "RV Living",
    image: rvLivingAsset.url,
    items: ["RV Decorations", "RV Games & Toys", "RV Pet Supplies", "Grills & Griddles", "Picnic Supplies", "Coolers"],
  },
  {
    title: "RV Parts",
    image: rvPartsImgAsset.url,
    items: ["RV Wheels", "RV Tires", "RV Brakes", "RV Fans", "RV Vents", "RV Vent Covers"],
  },
  {
    title: "RV Electrical",
    image: rvElectricalAsset.url,
    items: ["RV Generators", "RV Surge Protection", "RV Solar Panels", "RV Lighting", "RV Wifi Connectivity", "RV Cellular Boosters"],
  },
  {
    title: "RV Towing",
    image: rvTowingAsset.url,
    items: ["RV Jacks & Levelers", "Fifth Wheel Hitches", "Weight Distribution Hitches", "RV Tow Bars", "RV Car Dollies", "Braking Systems"],
  },
  {
    title: "RV Accessories",
    image: rvAccessoriesAsset.url,
    items: ["RV Covers", "RV Bike Racks", "RV Awnings"],
  },
];

const brands = [
  { name: "Blue Ox", logo: brandBlueoxAsset.url, href: "https://rexandsonsrvparts.com/50-blue-ox" },
  { name: "Dicor", logo: brandDicorAsset.url, href: "https://rexandsonsrvparts.com/124-dicor" },
  { name: "Lippert", logo: brandLippertAsset.url, href: "https://rexandsonsrvparts.com/251-lippert" },
  { name: "Thetford", logo: brandThetfordAsset.url, href: "https://rexandsonsrvparts.com/415-thetford" },
];

const partsCmsSlugs = ["rv-parts", "parts"];

// The /rv-parts page is maintained in the Pages admin as a custom page under
// this slug. When it exists, its content takes precedence over the legacy
// hard-coded sections below.
const CUSTOM_PAGE_SLUG = "rex-sons-rv-parts-wilmington-nc";

const pickPreferredPartsContent = <T extends { page_slug?: string | null }>(rows: T[] | null | undefined) => {
  if (!rows?.length) return null;
  return rows.find((row) => row.page_slug === "rv-parts") ?? rows[0];
};

const Parts = () => {
  // CMS-maintained custom page (Pages admin → rex-sons-rv-parts-wilmington-nc).
  // This is the content the dealer edits; when present it replaces the legacy
  // hard-coded page below.
  const { data: customPage } = useQuery({
    queryKey: ["custom-page", CUSTOM_PAGE_SLUG],
    queryFn: async () => {
      const { data } = await (await getSupabase())
        .from("custom_pages")
        .select("*")
        .eq("slug", CUSTOM_PAGE_SLUG)
        .eq("is_published", true)
        .maybeSingle();
      return data;
    },
  });

  const { data: customContentRow } = useQuery({
    queryKey: ["public-custom-page-content", CUSTOM_PAGE_SLUG],
    queryFn: async () => {
      const { data } = await (await getSupabase())
        .from("page_content")
        .select("*")
        .eq("page_slug", CUSTOM_PAGE_SLUG)
        .in("section_key", ["landing_page", "full_page"])
        .eq("is_published", true);
      const landing = data?.find((r) => r.section_key === "landing_page");
      return landing ?? data?.find((r) => r.section_key === "full_page") ?? null;
    },
  });

  const customContent = (customContentRow?.content ?? null) as Record<string, any> | null;
  const isLanding = customContentRow?.section_key === "landing_page";
  const customBody = (customContent?.['body'] as string | undefined) || "";
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);

  // Saved body HTML is sanitized, so inline scripts never run. The RV Part
  // Finder embed is mounted here from a fixed trusted URL whenever the body
  // contains its #rvChatContainer placeholder.
  useEffect(() => {
    const el = bodyEl;
    if (!el || !customBody) return;
    const host = el.querySelector<HTMLElement>("#rvChatContainer");
    if (!host || host.querySelector("iframe")) return;
    const timer = window.setTimeout(() => {
      if (host.querySelector("iframe")) return;
      const iframe = document.createElement("iframe");
      iframe.src = "https://rv-part-finder.lovable.app/";
      iframe.title = "RV Part Finder";
      iframe.style.cssText =
        "width:100%;max-width:1114px;height:870px;border:none;border-radius:12px;box-shadow:rgba(0,0,0,0.15) 0px 4px 20px;background:#ffffff;display:block;margin:0 auto";
      host.appendChild(iframe);
    }, 3500);
    return () => window.clearTimeout(timer);
  }, [customBody, bodyEl]);

  useEffect(() => {
    if (customContent?.['custom_scripts']) {
      try { new Function(customContent['custom_scripts'])(); } catch (e) { console.error("Parts custom script error:", e); }
    }
  }, [customContent?.['custom_scripts']]);

  const { data: heroContent } = useQuery({
    queryKey: ["page-content", "parts-route", "hero-content"],
    queryFn: async () => {
      const { data } = await (await getSupabase())
        .from("page_content")
        .select("page_slug, section_key, content")
        .in("page_slug", partsCmsSlugs)
        .in("section_key", ["hero_slides", "page_hero", "hero"])
        .eq("is_published", true)
        .order("updated_at", { ascending: false });

      const rows = data ?? [];
      const preferred =
        rows.find((row) => row.page_slug === "rv-parts" && row.section_key === "hero_slides") ??
        rows.find((row) => row.page_slug === "rv-parts" && row.section_key === "page_hero") ??
        rows.find((row) => row.page_slug === "rv-parts" && row.section_key === "hero") ??
        rows.find((row) => row.page_slug === "parts" && row.section_key === "hero_slides") ??
        rows.find((row) => row.page_slug === "parts" && row.section_key === "page_hero") ??
        rows.find((row) => row.page_slug === "parts" && row.section_key === "hero") ??
        rows[0];

      return preferred?.content as { slides?: Array<{ title?: string; subtitle?: string; image?: string; image_desktop?: string; image_mobile?: string; buttons?: any[] }> } | null;
    },
  });

  // CMS override — render Full Page Editor content saved against either the
  // route slug "rv-parts" or the legacy admin slug "parts".
  const { data: cmsFull } = useQuery({
    queryKey: ["page-content", "parts-route", "full_page"],
    queryFn: async () => {
      const { data } = await (await getSupabase())
        .from("page_content")
        .select("page_slug, content")
        .in("page_slug", partsCmsSlugs)
        .eq("section_key", "full_page")
        .order("page_slug", { ascending: false });
      return (pickPreferredPartsContent(data)?.content ?? null) as { body?: string; custom_css?: string; custom_scripts?: string } | null;
    },
  });

  useEffect(() => {
    if (cmsFull?.custom_scripts) {
      try { new Function(cmsFull.custom_scripts)(); } catch (e) { console.error("Parts custom script error:", e); }
    }
  }, [cmsFull?.custom_scripts]);

  const slide = heroContent?.slides?.[0];
  const heroTitle = slide?.title || "Rex & Sons RV Parts";
  const heroSubtitle = slide?.subtitle || "Start saving now on the RV essentials for your next RV adventure";
  const heroBg = slide?.image_desktop || slide?.image || STATIC_ASSETS.heroService1920;
  const heroBgMobile = slide?.image_mobile || STATIC_ASSETS.heroService640;
  const heroButtons = slide?.buttons;

  // CMS-maintained custom page takes precedence over the legacy hard-coded
  // sections. Landing-page variant renders the block template; full_page
  // renders hero fields + raw body (admin-authored, same as /p/$slug).
  if (customPage && customContent) {
    const pageTitle = (customPage['title'] as string) || "RV Parts & Accessories";
    const metaDesc = (customPage['meta_description'] as string) ||
      "Shop thousands of RV parts and accessories at Rex & Sons RVs. Nationwide shipping, 30+ years experience.";
    const head = (
      <Helmet>
        <title>{pageTitle} | Rex & Sons RVs</title>
        <meta name="description" content={metaDesc} />
      </Helmet>
    );

    if (isLanding) {
      return (
        <Layout>
          {head}
          <LandingPageTemplate content={customContent as unknown as LandingPageContent} pageSlug={CUSTOM_PAGE_SLUG} />
        </Layout>
      );
    }

    const cpHeroDesktop = (customPage['hero_image_desktop'] as string | null) || heroBg;
    const cpHeroMobile = (customPage['hero_image_mobile'] as string | null) || heroBgMobile;
    const cpHeroTitle = (customPage['hero_title'] as string | null) || heroTitle;
    const cpHeroSubtitle = (customPage['hero_subtitle'] as string | null) || heroSubtitle;
    const cpHeroButtons = Array.isArray(customPage['hero_buttons'])
      ? (customPage['hero_buttons'] as HeroButton[])
      : heroButtons;

    return (
      <Layout>
        {head}
        {customContent['custom_css'] && <style dangerouslySetInnerHTML={{ __html: customContent['custom_css'] }} />}
        <PageHero
          title={cpHeroTitle}
          subtitle={cpHeroSubtitle}
          backgroundImage={cpHeroDesktop}
          backgroundImageMobile={cpHeroMobile}
        >
          <HeroButtons {...(cpHeroButtons !== undefined ? { buttons: cpHeroButtons } : {})} />
        </PageHero>
        <div className="container py-12">
          <div ref={setBodyEl} dangerouslySetInnerHTML={{ __html: sanitizeHtml(customBody) }} />
        </div>
      </Layout>
    );
  }

  if (cmsFull?.body) {
    const safe = DOMPurify.sanitize(cmsFull.body, {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "target"],
    });
    return (
      <Layout>
        <Helmet>
          <title>RV Parts & Accessories | Rex & Sons RVs</title>
          <meta
            name="description"
            content="Shop thousands of RV parts and accessories at Rex & Sons RVs. Nationwide shipping, 30+ years experience."
          />
        </Helmet>
        {cmsFull.custom_css && <style dangerouslySetInnerHTML={{ __html: cmsFull.custom_css }} />}
        <PageHero
          title={heroTitle}
          subtitle={heroSubtitle}
          backgroundImage={heroBg}
          backgroundImageMobile={heroBgMobile}
        >
          <HeroButtons {...(heroButtons !== undefined ? { buttons: heroButtons } : {})} />
        </PageHero>
        <div
          className="prose prose-neutral max-w-none container mx-auto px-4 py-10"
          dangerouslySetInnerHTML={{ __html: safe }}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Title/description/canonical/og live in the route head() in
          src/routes/rv-parts.tsx — the single SSR-visible source. */}

      <PageHero
        title={heroTitle}
        subtitle={heroSubtitle}
        backgroundImage={heroBg}
        backgroundImageMobile={heroBgMobile}
      >
        <HeroButtons {...(heroButtons !== undefined ? { buttons: heroButtons } : {})} />
      </PageHero>

      {/* Welcome banner */}
      <section className="pt-10">
        <div className="container text-center">
          <img
            src={funInSunAsset.url}
            alt="Fun in the sun with Rex & Sons RV Parts – seasonal savings on popular summer RV parts and accessories"
            width={1200}
            height={628}
            className="mx-auto max-w-full h-auto rounded-xl"
            loading="lazy"
          />
          <p className="mx-auto mt-10 max-w-3xl text-lg text-muted-foreground leading-relaxed">
            No matter what your needs are, Rex &amp; Sons RV Parts can help. With nationwide shipping, we have you covered. Come check out all the savings on our most popular summer items!
          </p>
        </div>
      </section>

      {/* Category Grid */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="pb-16">
        <div className="container">
          <div
            className="mx-auto grid gap-6"
            style={{
              maxWidth: 1200,
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            }}
          >
            {categories.map((cat) => (
              <div key={cat.title} className="parts-item">
                <img
                  src={cat.image}
                  alt={cat.title}
                  loading="lazy"
                  className="w-full h-auto rounded-md"
                />
                <h2 className="mt-3 text-center font-heading text-xl font-bold uppercase text-foreground">
                  {cat.title}
                </h2>
                <ul className="mt-2 list-disc pl-6 text-muted-foreground space-y-1">
                  {cat.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
</LazySection>

      {/* About Copy */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="pb-16">
        <div className="container">
          <div className="mx-auto max-w-4xl rounded-xl border bg-card p-8 shadow-xs">
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Rex & Sons offers all the parts you'll need for your RV or camping, at affordable prices! Stop by today and visit our RV Parts & Accessories department.
              </p>
              <p>
                Visit Rex and Sons RV Parts & Gear site for all your camping needs on the road or at home — we're there when you need us. With over 30 years of parts sales experience and over 20 shipping locations across the country for fast delivery.
              </p>
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary" />
                Call us today{" "}
                <a href="tel:9106796522" className="font-semibold text-primary hover:underline">(910) 679-6522</a>
              </p>
              <p>
                We proudly carry a large selection of Class A and Class B Motorhomes, Travel Trailers and Toy Haulers. Offering units from a range of industry-leading manufacturers such as Coachmen, InTech, Winnebago, Venture RV and more!
              </p>
              <p>
                With our highly trained and professional team, we are focused on satisfying our customers needs. Stop by our location in Wilmington, NC today to check out our sales, service, parts and rental departments or take a look at our online parts and accessories store right here!
              </p>
              <p>
                Thousands of RV parts and accessories. Knowledgeable staff who can answer your questions. Professional installation by certified RV technicians. You'll find it all under one roof at Rex & Sons RVs. Whether you're a weekend warrior or a full-time RVer, our Supercenter has everything you need to make your travels more enjoyable: essential towing equipment, the latest mobile connectivity technology, safety devices, lifestyle accessories, and more!
              </p>
              <div className="pt-4 text-center">
                <Button asChild size="lg" className="font-heading uppercase">
                  <Link to="/parts-catalog">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Shop Thousands of RV Parts & Accessories
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
</LazySection>

      {/* Brand Logos */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="pb-16">
        <div className="container">
          <h2 className="font-heading text-2xl font-bold uppercase text-foreground text-center mb-2">
            We Carry the Top RV Parts & Accessory Brands
          </h2>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Carrying the top brands of RV parts and accessories is part of our commitment to being your premier RV dealer.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {brands.map((brand) => (
              <a
                key={brand.name}
                href={brand.href}
                target="_blank"
                rel="noopener noreferrer"
                className="grayscale hover:grayscale-0 transition duration-300"
              >
                <img src={brand.logo} alt={brand.name} className="h-12 sm:h-16 object-contain" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      </section>
</LazySection>

      {/* CTA */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="bg-primary py-10">
        <div className="container text-center">
          <h2 className="font-heading text-2xl font-bold uppercase text-primary-foreground">Need RV Parts?</h2>
          <p className="mt-2 text-primary-foreground">Call our parts department or shop online for nationwide shipping.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Button asChild variant="secondary" size="lg" className="font-heading uppercase">
              <a href="tel:9106796522">
                <Phone className="mr-2 h-4 w-4" />
                Call (910) 679-6522
              </a>
            </Button>
            <Button asChild size="lg" className="font-heading uppercase bg-primary-foreground text-primary hover:bg-primary-foreground/90">
              <Link to="/parts-catalog">
                <ExternalLink className="mr-2 h-4 w-4" />
                Shop Online
              </Link>
            </Button>
          </div>
        </div>
      </section>
</LazySection>
    </Layout>
  );
};

export default Parts;
